import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import { Shield, FileText, AlertTriangle, Lock } from "lucide-react";

const TX = {
  bg: {
    title: "Правни политики и GDPR", back: "Начало",
    privacy_title: "Политика за поверителност (GDPR)",
    privacy_intro: "Nutramilo, управляван от International Sci Ink Press Ltd., обработва лични данни в съответствие с Общия регламент за защита на данните (GDPR) на Европейския съюз.",
    q1: "Какви данни събираме?", a1: "Събираме данни, които вие доброволно предоставяте: име, email, профилни данни (тегло, ръст, възраст, пол), лабораторни стойности, хранителни дневници и история на AI консултациите. Тези данни се използват единствено за персонализиране на вашето изживяване в платформата.",
    q2: "Как използваме данните?", a2: "Данните се използват за изчисляване на здравни индекси, персонализиране на AI консултации и генериране на хранителни планове. Не споделяме данните ви с трети страни за маркетингови цели.",
    q3: "Вашите права по GDPR", a3: "Имате право на достъп, коригиране и изтриване на личните си данни. Може да поискате експорт на данните си по всяко време. За упражняване на тези права, свържете се с нас чрез контактната форма.",
    q4: "Съхранение и сигурност", a4: "Данните се съхраняват криптирани в облачна инфраструктура. Паролите са хеширани с bcrypt. Използваме HTTPS за всички комуникации.",
    ip_title: "Интелектуална собственост",
    ip_p1: "Всички алгоритми, формули, модели за изчисление (включително, но не само: DIL модел, DII индекс, Endo-Score, Satiety Score, Nutrient Density Score, метаболитни фенотипи P1-P5, циклови модификатори и Structure-Fit), както и техните реализации в софтуерния код, са обект на интелектуална собственост на International Sci Ink Press Ltd.",
    ip_p2: "Забранено е копирането, декомпилирането, реверсивното инженерство или възпроизвеждането на тези модели и алгоритми без писмено съгласие на собственика. Нарушаването подлежи на преследване по приложимите закони за интелектуална собственост.",
    ip_p3: "Потребителското съдържание (данни, резултати, отчети), генерирано от платформата, е предназначено само за лична употреба и не може да се комерсиализира или разпространява без разрешение.",
    ip_p4: "Марката Nutramilo, логото и визуалната идентичност са защитени търговски марки.",
    terms_title: "Условия за ползване",
    terms_p1: "Nutramilo предоставя информация за метаболитно и хранително здраве, генерирана от AI системи. Тази информация НЕ е медицинска диагноза и НЕ замества консултация с лекар.",
    terms_p2: "Потребителите са отговорни за точността на данните, които въвеждат. Nutramilo не носи отговорност за решения, взети на базата на AI препоръките без консултация с квалифициран специалист.",
    terms_p3: "Закупените точки не подлежат на връщане. Nutramilo си запазва правото да променя ценовата политика с предварително уведомление.",
    disclaimer_title: "Отказ от отговорност",
    disclaimer: "Nutramilo не е медицинско изделие. AI асистентите действат като изследователски инструменти, а не като заместители на медицински специалисти. Винаги консултирайте се с вашия лекар преди да правите промени в диетата или лечението си.",
    updated: "Последна актуализация: Април 2026",
    seo_desc: "GDPR политика, условия за ползване и защита на лични данни в Nutramilo.",
  },
  en: {
    title: "Legal Policies & GDPR", back: "Home",
    privacy_title: "Privacy Policy (GDPR)",
    privacy_intro: "Nutramilo, operated by International Sci Ink Press Ltd., processes personal data in accordance with the European Union's General Data Protection Regulation (GDPR).",
    q1: "What data do we collect?", a1: "We collect data you voluntarily provide: name, email, profile data (weight, height, age, gender), lab values, meal logs, and AI consultation history. This data is used solely to personalize your platform experience.",
    q2: "How do we use the data?", a2: "Data is used to calculate health indices, personalize AI consultations, and generate meal plans. We do not share your data with third parties for marketing purposes.",
    q3: "Your GDPR Rights", a3: "You have the right to access, correct, and delete your personal data. You can request data export at any time. To exercise these rights, reach us through the contact form.",
    q4: "Storage & Security", a4: "Data is stored encrypted in cloud infrastructure. Passwords are hashed with bcrypt. We use HTTPS for all communications.",
    ip_title: "Intellectual Property",
    ip_p1: "All algorithms, formulas, calculation models (including but not limited to: DIL model, DII index, Endo-Score, Satiety Score, Nutrient Density Score, metabolic phenotypes P1-P5, cycle modifiers and Structure-Fit), as well as their software implementations, are the intellectual property of International Sci Ink Press Ltd.",
    ip_p2: "Copying, decompiling, reverse engineering or reproducing these models and algorithms without written consent from the owner is prohibited. Violations are subject to prosecution under applicable intellectual property laws.",
    ip_p3: "User content (data, results, reports) generated by the platform is intended for personal use only and may not be commercialized or distributed without permission.",
    ip_p4: "The Nutramilo brand, logo, and visual identity are protected trademarks.",
    terms_title: "Terms of Service",
    terms_p1: "Nutramilo provides metabolic and nutritional health information generated by AI systems. This information is NOT a medical diagnosis and does NOT replace consultation with a doctor.",
    terms_p2: "Users are responsible for the accuracy of the data they enter. Nutramilo is not liable for decisions made based on AI recommendations without consulting a qualified specialist.",
    terms_p3: "Purchased points are non-refundable. Nutramilo reserves the right to change pricing with prior notice.",
    disclaimer_title: "Disclaimer",
    disclaimer: "Nutramilo is not a medical device. AI assistants act as research tools, not as substitutes for medical professionals. Always consult your doctor before making changes to your diet or treatment.",
    updated: "Last updated: April 2026",
    seo_desc: "GDPR policy, terms of use and data protection in Nutramilo.",
  },
  es: {
    title: "Politicas legales y GDPR", back: "Inicio",
    privacy_title: "Politica de privacidad (GDPR)",
    privacy_intro: "Nutramilo, operado por International Sci Ink Press Ltd., procesa datos personales de acuerdo con el Reglamento General de Proteccion de Datos (GDPR) de la Union Europea.",
    q1: "Que datos recopilamos?", a1: "Recopilamos datos que usted proporciona voluntariamente: nombre, email, datos de perfil (peso, altura, edad, genero), valores de laboratorio, registros de comidas e historial de consultas con IA.",
    q2: "Como usamos los datos?", a2: "Los datos se utilizan para calcular indices de salud, personalizar consultas de IA y generar planes alimentarios. No compartimos sus datos con terceros para fines de marketing.",
    q3: "Sus derechos GDPR", a3: "Tiene derecho a acceder, corregir y eliminar sus datos personales. Puede solicitar la exportacion de sus datos en cualquier momento.",
    q4: "Almacenamiento y seguridad", a4: "Los datos se almacenan cifrados en infraestructura en la nube. Las contrasenas se hashean con bcrypt. Usamos HTTPS para todas las comunicaciones.",
    ip_title: "Propiedad intelectual",
    ip_p1: "Todos los algoritmos, formulas y modelos de calculo (incluyendo: modelo DIL, indice DII, Endo-Score, Satiety Score, Nutrient Density Score, fenotipos metabolicos P1-P5) son propiedad intelectual de International Sci Ink Press Ltd.",
    ip_p2: "Se prohibe copiar, descompilar, realizar ingenieria inversa o reproducir estos modelos y algoritmos sin consentimiento escrito del propietario.",
    ip_p3: "El contenido generado por la plataforma esta destinado solo para uso personal y no puede ser comercializado o distribuido sin permiso.",
    ip_p4: "La marca Nutramilo, el logotipo y la identidad visual son marcas comerciales protegidas.",
    terms_title: "Terminos de servicio",
    terms_p1: "Nutramilo proporciona informacion de salud metabolica y nutricional generada por sistemas de IA. Esta informacion NO es un diagnostico medico y NO reemplaza la consulta con un medico.",
    terms_p2: "Los usuarios son responsables de la precision de los datos que ingresan. Nutramilo no es responsable de decisiones tomadas basandose en recomendaciones de IA sin consultar a un especialista calificado.",
    terms_p3: "Los puntos comprados no son reembolsables. Nutramilo se reserva el derecho de cambiar los precios con aviso previo.",
    disclaimer_title: "Descargo de responsabilidad",
    disclaimer: "Nutramilo no es un dispositivo medico. Los asistentes de IA actuan como herramientas de investigacion, no como sustitutos de profesionales medicos. Siempre consulte a su medico antes de realizar cambios en su dieta o tratamiento.",
    updated: "Ultima actualizacion: Abril 2026",
    seo_desc: "Politica GDPR, terminos de uso y proteccion de datos en Nutramilo.",
  },
};

export default function LegalPage({ variant = "all", pathOverride }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX[language === "bg" ? "bg" : "en"] || TX.en;

  const sectionCard = `p-6 rounded-2xl border ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,14%)]' : 'bg-white border-slate-100 shadow-sm'}`;

  // iter881 — variants for Google OAuth consent screen dedicated URLs.
  const showPrivacy = variant === "all" || variant === "privacy-only";
  const showIP      = variant === "all" || variant === "terms-only";
  const showTerms   = variant === "all" || variant === "terms-only";
  const showAIMeth  = variant === "all" || variant === "privacy-only";

  const pageTitle = (
    variant === "privacy-only" ? (language === "bg" ? "Политика за поверителност" : "Privacy Policy") :
    variant === "terms-only"   ? (language === "bg" ? "Условия за ползване" : "Terms of Service") :
    t.title
  );
  const pageDesc = (
    variant === "privacy-only" ? (language === "bg"
      ? "Пълна GDPR политика за поверителност, вашите права, срокове за съхранение и списък на подизпълнителите."
      : "Full GDPR privacy policy, your rights, retention periods, and list of subprocessors.") :
    variant === "terms-only" ? (language === "bg"
      ? "Условия за ползване на Nutramilo + защита на интелектуалната собственост."
      : "Terms of service for Nutramilo + intellectual property protection.") :
    t.seo_desc
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-background text-foreground' : 'bg-[#F9F9F7]'}`} data-testid="legal-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={pageTitle} description={pageDesc} path={pathOverride || "/legal"} />
      <SubpageNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <SubpageBackLink label={t.back} />

        <SubpageHero
          eyebrow={language === "bg" ? "Правна рамка" : language === "es" ? "Marco legal" : "Legal framework"}
          title={pageTitle}
          subtitle={pageDesc}
          meta={
            language === "bg"
              ? ["Администратор: International Sci Ink Press Ltd", "Съответствие с GDPR", "Образователна платформа"]
              : language === "es"
              ? ["Responsable: International Sci Ink Press Ltd", "Conforme al RGPD", "Plataforma educativa"]
              : ["Controller: International Sci Ink Press Ltd", "GDPR compliant", "Educational platform"]
          }
          icon={Shield}
          accent="#6366F1"
          testId="legal-hero"
        />
        <div className="mb-10" />

        {/* Privacy / GDPR */}
        {showPrivacy && (
        <section className="mb-10" data-testid="privacy-section">
          <div className="flex items-center gap-2.5 mb-4">
            <Shield className="w-5 h-5 text-[#6366F1]" />
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {t.privacy_title}
            </h2>
          </div>
          <div className={`text-sm leading-relaxed space-y-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            <p>{t.privacy_intro}</p>
            {[[t.q1, t.a1], [t.q2, t.a2], [t.q3, t.a3], [t.q4, t.a4]].map(([q, a]) => (
              <div key={q}>
                <h3 className={`text-sm font-bold mt-5 mb-1 ${isDark ? 'text-slate-200' : 'text-[#1A1A2E]'}`}>{q}</h3>
                <p>{a}</p>
              </div>
            ))}
          </div>
        </section>
        )}

        {/* IP */}
        {showIP && (
        <section className="mb-10" data-testid="ip-section">
          <div className="flex items-center gap-2.5 mb-4">
            <Lock className="w-5 h-5 text-[#F59E0B]" />
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {t.ip_title}
            </h2>
          </div>
          <div className={`text-sm leading-relaxed space-y-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            <p>{t.ip_p1}</p><p>{t.ip_p2}</p><p>{t.ip_p3}</p><p>{t.ip_p4}</p>
          </div>
        </section>
        )}

        {/* Terms */}
        {showTerms && (
        <section className="mb-10" data-testid="terms-section">
          <div className="flex items-center gap-2.5 mb-4">
            <FileText className="w-5 h-5 text-[#0EA5E9]" />
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {t.terms_title}
            </h2>
          </div>
          <div className={`text-sm leading-relaxed space-y-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            <p>{t.terms_p1}</p><p>{t.terms_p2}</p><p>{t.terms_p3}</p>
          </div>
        </section>
        )}

        {/* iter304 — AI Methodology + Subprocessors (GDPR Art. 13/14 + EU AI Act Art. 13) */}
        {showAIMeth && (
        <section className="mb-10" id="ai-methodology" data-testid="ai-methodology-section">
          <div className="flex items-center gap-2.5 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#8B5CF6]" />
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {language === "bg" ? "AI методология и подизпълнители" : "AI methodology & subprocessors"}
            </h2>
          </div>
          <div className={sectionCard}>
            <div className={`space-y-4 text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              <div>
                <h3 className="font-bold mb-1">{language === "bg" ? "Използвани AI модели" : "AI models used"}</h3>
                <p>{language === "bg"
                  ? "Nutramilo използва големи езикови модели (LLM) чрез Emergent интеграционен слой: OpenAI GPT-5.2 · Anthropic Claude Sonnet 4.5 · Google Gemini 3. За изчисления и симулации се използват прозрачни rule-based алгоритми (напр. CKD-EPI 2021, PhenoAge, KFRE)."
                  : "Nutramilo uses large language models via the Emergent integration layer: OpenAI GPT-5.2 · Anthropic Claude Sonnet 4.5 · Google Gemini 3. Calculations and simulations use transparent rule-based algorithms (e.g. CKD-EPI 2021, PhenoAge, KFRE)."}
                </p>
              </div>
              <div>
                <h3 className="font-bold mb-1">{language === "bg" ? "Подизпълнители (subprocessors)" : "Subprocessors"}</h3>
                <ul className="list-disc list-inside space-y-0.5 text-[13px]">
                  <li>OpenAI Ireland Ltd (EU) / OpenAI LLC (US) — GPT inference · SCC 2021/914</li>
                  <li>Anthropic PBC (US) — Claude inference · SCC 2021/914</li>
                  <li>Google Ireland Ltd (EU) — Gemini inference · SCC + EU-US DPF</li>
                  <li>Stripe Payments Europe Ltd (IE) — payment processing</li>
                  <li>Wise Europe SA (BE) — alternative payment processing</li>
                  <li>Twilio SendGrid (US) — transactional email · SCC 2021/914</li>
                  <li>MongoDB Atlas (EU-hosted) — primary database (Frankfurt region)</li>
                  <li>Emergent Labs — hosting + integration layer (EU region)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold mb-1">{language === "bg" ? "Срокове за съхранение" : "Retention periods"}</h3>
                <ul className="list-disc list-inside space-y-0.5 text-[13px]">
                  <li>{language === "bg" ? "Активен акаунт — докато не поискате изтриване" : "Active account — until you request deletion"}</li>
                  <li>{language === "bg" ? "Здравни данни — 1 година след изтриване на акаунта (резервни копия)" : "Health data — 1 year after account deletion (backups)"}</li>
                  <li>{language === "bg" ? "Финансови записи (фактури) — 10 години (счетоводно законодателство)" : "Financial records (invoices) — 10 years (accounting law)"}</li>
                  <li>{language === "bg" ? "AI inference audit log — 6 месеца (GDPR Чл. 30, AI Act Чл. 12)" : "AI inference audit log — 6 months (GDPR Art. 30, AI Act Art. 12)"}</li>
                  <li>{language === "bg" ? "Временни данни (password reset, email verification) — до 24 часа" : "Temporary data (password reset, email verification) — up to 24 hours"}</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold mb-1">{language === "bg" ? "Вашите права (GDPR)" : "Your rights (GDPR)"}</h3>
                <ul className="list-disc list-inside space-y-0.5 text-[13px]">
                  <li>{language === "bg" ? "Чл. 15 — достъп до всичките ви данни · бутон „Export“ в Profile" : "Art. 15 — access to all your data · 'Export' button in Profile"}</li>
                  <li>{language === "bg" ? "Чл. 17 — право на изтриване · бутон „Delete account“ в Profile" : "Art. 17 — right to erasure · 'Delete account' button in Profile"}</li>
                  <li>{language === "bg" ? "Чл. 20 — преносимост на данни (машинно-четим формат)" : "Art. 20 — data portability (machine-readable format)"}</li>
                  <li>{language === "bg" ? "Чл. 22 — обяснение на автоматизирани решения · бутон „Защо?“ до всеки AI резултат" : "Art. 22 — explanation of automated decisions · 'Why?' button next to every AI output"}</li>
                  <li>{language === "bg" ? "Право на жалба пред КЗЛД (България) или друг EU регулатор" : "Right to lodge a complaint with your supervisory authority"}</li>
                </ul>
              </div>
              <div className={`rounded-lg p-3 mt-3 ${isDark ? 'bg-amber-950/30 border border-amber-800/40' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-[12px] italic ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  {language === "bg"
                    ? "⚕️ Nutramilo НЕ е медицинско изделие по смисъла на MDR 2017/745. AI изходите са образователни — не заместват медицинска консултация или диагноза."
                    : "⚕️ Nutramilo is NOT a medical device within the meaning of MDR 2017/745. AI outputs are educational — not a substitute for medical consultation or diagnosis."}
                </p>
              </div>
              <p className="text-[11px] opacity-70">
                {language === "bg" ? "DPO контакт: privacy@nutramilo.bg" : "DPO contact: privacy@nutramilo.bg"} · v1.0-2026-02
              </p>
            </div>
          </div>
        </section>
        )}

        {/* Disclaimer */}
        <section className="mb-10" data-testid="disclaimer-section">
          <div className="flex items-center gap-2.5 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {t.disclaimer_title}
            </h2>
          </div>
          <div className={sectionCard}>
            <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{t.disclaimer}</p>
          </div>
        </section>

        {/* iter384 — Controller identification + DPO contact + Complaint right (GDPR Art. 13(1)(a-d) + Art. 77) */}
        <section className="mb-10" data-testid="controller-section">
          <div className={`${sectionCard}`}>
            <h3 className={`text-sm font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
              {language === "bg" ? "Администратор на данни и контакт" : "Data Controller & contact"}
            </h3>
            <div className={`text-sm space-y-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              <p><b>{language === "bg" ? "Администратор:" : "Controller:"}</b> International Sci Ink Press Ltd.</p>
              <p><b>EИК:</b> 205414288</p>
              <p><b>{language === "bg" ? "ДДС №:" : "VAT No.:"}</b> BG205414288</p>
              <p><b>{language === "bg" ? "Адрес на седалище:" : "Registered office:"}</b> {language === "bg" ? `БЪЛГАРИЯ, гр. София (1303), р-н Възраждане, ул. „Антим I" № 57, ет. 1` : "57 Antim I Str., Floor 1, Vazrazhdane District, Sofia 1303, Bulgaria"}</p>
              <p><b>Email:</b> dpo@nutramilo.com · privacy@nutramilo.com</p>
              <p className="mt-3"><b>{language === "bg" ? "Длъжностно лице по защита на данните (DPO):" : "Data Protection Officer (DPO):"}</b> dpo@nutramilo.com</p>
              <p className="mt-3">
                <b>{language === "bg" ? "Право на жалба:" : "Right to lodge a complaint:"}</b>{" "}
                {language === "bg"
                  ? "Имате право да подадете жалба до Комисията за защита на личните данни (КЗЛД) на адрес "
                  : "You have the right to lodge a complaint with the Bulgarian Personal Data Protection Commission (CPDP) at "}
                <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer" className="text-[#0EA5E9] underline">www.cpdp.bg</a>.
              </p>
              <p className="mt-3">
                <b>{language === "bg" ? "Право на отказ (14 дни):" : "Right of withdrawal (14 days):"}</b>{" "}
                {language === "bg"
                  ? "Съгласно Директива 2011/83/ЕС имате право да се откажете от платен абонамент в първите 14 дни без посочване на причина — пълно възстановяване на сумата."
                  : "Per EU Directive 2011/83/EU you may cancel a paid subscription within 14 days without reason — full refund."}
              </p>
            </div>
          </div>
        </section>

        <div className={`text-center text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          <p>{t.updated}</p>
          <p className="mt-1">International Sci Ink Press Ltd.</p>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}
